import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Briefcase, Clock } from "@phosphor-icons/react";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "", linkedin: "", experience_years: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/careers/jobs").then((r)=>setJobs(r.data)); }, []);

  const apply = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/careers/apply", { ...form, job_id: selected.id, experience_years: form.experience_years ? Number(form.experience_years) : null });
      toast.success("Application submitted. We'll be in touch.");
      setSelected(null);
      setForm({ name: "", email: "", phone: "", cover_letter: "", linkedin: "", experience_years: "" });
    } catch (err) { toast.error(formatErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="overline text-gold-dark mb-6">Careers</div>
      <h1 className="font-display text-5xl md:text-6xl text-midnight tracking-tight mb-6">Join a firm that plays the <span className="italic">long game</span>.</h1>
      <p className="text-midnight/70 max-w-2xl mb-14">We hire quiet, curious professionals who believe good advice compounds. If that's you, we'd love to talk.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {jobs.map((j) => (
          <div key={j.id} className="bg-white rounded-2xl p-8 border border-black/5">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="font-display text-2xl text-midnight">{j.title}</h3>
                <div className="text-xs text-midnight/60 mt-1">{j.department}</div>
              </div>
              <span className="overline text-gold-dark text-[10px] px-3 py-1 rounded-full bg-gold/10">{j.type}</span>
            </div>
            <p className="text-sm text-midnight/70 leading-relaxed mb-6">{j.description}</p>
            <div className="flex flex-wrap gap-5 text-xs text-midnight/60 mb-6">
              <span className="flex items-center gap-1.5"><MapPin size={14} /> {j.location}</span>
              <span className="flex items-center gap-1.5"><Briefcase size={14} /> {j.department}</span>
              <span className="flex items-center gap-1.5"><Clock size={14} /> {j.type}</span>
            </div>
            <button data-testid={`apply-btn-${j.id}`} onClick={() => setSelected(j)} className="btn-outline-ink text-sm">Apply</button>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-midnight/60 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={()=>setSelected(null)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={apply} data-testid="apply-form" className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-auto">
            <div className="overline text-gold-dark mb-2">Application</div>
            <h3 className="font-display text-2xl text-midnight mb-6">Apply for {selected.title}</h3>
            <div className="space-y-4">
              <input required placeholder="Full name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="apply-name" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="apply-email" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm" />
              <input required placeholder="Phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="apply-phone" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm" />
              <input placeholder="LinkedIn URL" value={form.linkedin} onChange={(e)=>setForm({...form,linkedin:e.target.value})} data-testid="apply-linkedin" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm" />
              <input type="number" placeholder="Years of experience" value={form.experience_years} onChange={(e)=>setForm({...form,experience_years:e.target.value})} data-testid="apply-exp" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm" />
              <textarea required rows={5} placeholder="Why us? Tell us briefly." value={form.cover_letter} onChange={(e)=>setForm({...form,cover_letter:e.target.value})} data-testid="apply-cover" className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm resize-none" />
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={()=>setSelected(null)} className="btn-outline-ink text-sm flex-1">Cancel</button>
              <button type="submit" disabled={loading} data-testid="apply-submit" className="btn-gold text-sm flex-1">{loading ? "Sending…" : "Submit"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
